import React from "react";
import parse from "html-react-parser";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Divider,
  Avatar,
  Card,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Link } from "react-router-dom";
import { MySelectedProductCats } from "./profile/MySelectedProductCats";
import InteractiveAction2 from "../../../widgets/InteractiveAction2";

const ProductDetail = ({ product }) => {
  return (
    <Box sx={{ maxWidth: 900, margin: "0 auto", mt: 2, mb: 4 }}>
      <Paper
        elevation={0}
        variant="outlined"
        sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}
      >
        <Grid container spacing={3}>
          {/* Left Column: Media */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {product.images && product.images.length > 0 && (
                <Card
                  elevation={0}
                  variant="outlined"
                  sx={{ borderRadius: 2, overflow: "hidden" }}
                >
                  <img
                    style={{
                      width: "100%",
                      display: "block",
                      objectFit: "contain",
                      maxHeight: "250px",
                    }}
                    src={product.images[0]}
                    alt={product.name}
                  />
                </Card>
              )}

              {product.productVideo && (
                <Card
                  elevation={0}
                  variant="outlined"
                  sx={{ borderRadius: 2, overflow: "hidden", p: 0.5 }}
                >
                  {product.productVideo.selection === "embed" ? (
                    <Box
                      sx={{
                        "& iframe": {
                          width: "100%",
                          height: "250px",
                          maxHeight: "250px",
                          borderRadius: "8px",
                          display: "block",
                        },
                      }}
                    >
                      {parse(product.productVideo.content)}
                    </Box>
                  ) : (
                    <Box>
                      <video
                        controls
                        style={{
                          width: "100%",
                          height: "250px",
                          objectFit: "contain",
                          borderRadius: "8px",
                          display: "block",
                          backgroundColor: "black",
                        }}
                      >
                        <source
                          src={product.productVideo.content}
                          type="video/mp4"
                        />
                        Your browser does not support the video tag.
                      </video>
                    </Box>
                  )}
                </Card>
              )}
            </Box>
          </Grid>

          {/* Right Column: Product Details */}
          <Grid item xs={12} md={8}>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                color="text.primary"
                gutterBottom
              >
                {product.name}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Avatar
                  sx={{ bgcolor: "primary.main", mr: 1, width: 24, height: 24 }}
                >
                  <BusinessIcon sx={{ fontSize: 16 }} />
                </Avatar>
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  fontWeight="500"
                >
                  {product.exhibitor?.companyName?.value}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <LocationOnIcon sx={{ fontSize: 18 }} color="action" />
                <Link
                  to={`/hall-view/${product.exhibitor?.hallNumber?.value}?un=${product.exhibitor.username}`}
                  style={{ textDecoration: "none" }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      ml: 0.5,
                      color: "primary.main",
                      fontWeight: 600,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Hall {product.exhibitor?.hallNumber?.value} / Stand{" "}
                    {product.exhibitor?.standNumber?.value}
                  </Typography>
                </Link>
              </Box>

              <Box sx={{ mb: 2 }}>
                <InteractiveAction2
                  item={{ username: product.exhibitor.username }}
                  showDetail={false}
                />
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                Product Info
              </Typography>
              <Box
                sx={{
                  color: "text.secondary",
                  fontSize: "0.95rem",
                  lineHeight: 1.5,
                  mb: 2,
                }}
              >
                {parse(product.description || "")}
              </Box>

              {product.assignTo && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 2,
                    bgcolor: "grey.50",
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  <AssignmentIndIcon
                    color="action"
                    sx={{ mr: 1, fontSize: 20 }}
                  />
                  <Typography variant="body2" fontWeight="600" sx={{ mr: 1 }}>
                    Assigned to:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {parse(product.assignTo)}
                  </Typography>
                </Box>
              )}

              {product.brochure && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<PictureAsPdfIcon />}
                    component="a"
                    href={product.brochure}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textTransform: "none", borderRadius: 1 }}
                  >
                    Download Brochure
                  </Button>
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              <Box>
                <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                  Categories
                </Typography>
                <MySelectedProductCats
                  items={product.selectedCategories || []}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default ProductDetail;
